import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Check, Briefcase, Plane, Target, MessageSquare, BookOpen, Mic } from "lucide-react";
import { updateDailyGoal } from "../gamification";
import { useNativeLanguage } from "../contexts/NativeLanguageContext";
import { NATIVE_LANGUAGES } from "../data/multiLanguage";
import lessons from "../data/lessons.json";
import Logo from "../components/atoms/Logo";
import Mascot from "../components/atoms/Mascot";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

const STEPS = ["welcome", "reason", "native", "intro-test", "level", "goal", "batch-preview", "ready"];

const REASONS = [
  { id: "career",  label: "Career & work",        desc: "Professional English for the workplace.", Icon: Briefcase },
  { id: "travel",  label: "Travel & living",      desc: "Move, settle, hold a conversation.",       Icon: Plane },
  { id: "exam",    label: "IELTS / exam prep",    desc: "Pass the test, with a plan.",              Icon: Target },
  { id: "general", label: "General communication", desc: "Everyday, with confidence.",              Icon: MessageSquare },
];

const LEVELS = [
  { id: "beginner",     label: "Beginner",          desc: "I know very little English.",          startDay: 1,  spine: "var(--sage)",     tone: "sage" },
  { id: "elementary",   label: "Elementary",        desc: "I know basic words and sentences.",    startDay: 5,  spine: "var(--sage-dark)", tone: "sage" },
  { id: "intermediate", label: "Intermediate",      desc: "I can hold simple conversations.",     startDay: 13, spine: "var(--gold-500)", tone: "gold" },
  { id: "upper",        label: "Upper intermediate", desc: "I want to polish my grammar.",        startDay: 30, spine: "var(--rust)",     tone: "rust" },
];

const GOALS = [
  { minutes: 30,  label: "30 min/day",  desc: "Casual" },
  { minutes: 60,  label: "1 hr/day",    desc: "Regular" },
  { minutes: 120, label: "2 hr/day",    desc: "Serious", featured: true },
  { minutes: 180, label: "3 hr/day",    desc: "Intense" },
];

const INTRO_TEST = [
  { q: "Which is correct?",                                     options: ["She go to school", "She goes to school", "She going to school"], answer: 1, level: 0 },
  { q: "Pick the right article: ___ apple",                     options: ["a", "an", "the"],                                                  answer: 1, level: 0 },
  { q: "She ___ a book yesterday.",                             options: ["read", "reads", "reading"],                                        answer: 0, level: 1 },
  { q: "If I ___ rich, I would travel the world.",              options: ["am", "was", "were"],                                                answer: 2, level: 2 },
  { q: "By the time he arrived, we ___ already left.",          options: ["have", "had", "will have"],                                        answer: 1, level: 3 },
];

function recommendLevel(score) {
  if (score <= 1) return "beginner";
  if (score === 2) return "elementary";
  if (score <= 4) return "intermediate";
  return "upper";
}

export default function Onboarding({ onComplete }) {
  const navigate = useNavigate();
  const { code: nativeCode, setCode: setNativeCode } = useNativeLanguage();
  const [step, setStep] = useState(0);
  const [reason, setReason] = useState(null);
  const [level, setLevel] = useState(null);
  const [goal, setGoal] = useState(120);
  const [testAnswers, setTestAnswers] = useState({});
  const [testQ, setTestQ] = useState(0);

  function next() { if (step < STEPS.length - 1) setStep(step + 1); }
  function prev() { if (step > 0) setStep(step - 1); }

  const testScore = useMemo(
    () => Object.entries(testAnswers).filter(([qi, ai]) => INTRO_TEST[qi].answer === ai).length,
    [testAnswers]
  );

  const suggestedLevel = useMemo(() => {
    if (Object.keys(testAnswers).length < INTRO_TEST.length) return null;
    return recommendLevel(testScore);
  }, [testAnswers, testScore]);

  const selectedLevelObj = useMemo(() => LEVELS.find((l) => l.id === level), [level]);
  const startDay = selectedLevelObj?.startDay || 1;
  const batchLessons = useMemo(() => lessons.filter((l) => l.day >= startDay).slice(0, 6), [startDay]);

  function answerTest(qIdx, ansIdx) {
    setTestAnswers((p) => ({ ...p, [qIdx]: ansIdx }));
    setTimeout(() => {
      if (testQ < INTRO_TEST.length - 1) {
        setTestQ(testQ + 1);
      } else {
        const rec = recommendLevel(
          Object.entries({ ...testAnswers, [qIdx]: ansIdx }).filter(([qi, ai]) => INTRO_TEST[qi].answer === ai).length
        );
        setLevel(rec);
        setStep(step + 1);
      }
    }, 600);
  }

  function finish() {
    localStorage.setItem("onboarded", "true");
    localStorage.setItem("learningReason", reason || "general");
    localStorage.setItem("currentDay", String(startDay));
    updateDailyGoal(goal);
    if (onComplete) onComplete();
    const isLoggedIn = !!localStorage.getItem("currentUser");
    navigate(isLoggedIn ? "/dashboard" : "/signup");
  }

  const currentNative = NATIVE_LANGUAGES.find((l) => l.code === nativeCode);

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)" }}>
      <header style={{ padding: "20px 24px", maxWidth: 1080, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo /></Link>
        <Link to="/login" style={{ color: "var(--fg-3)", fontSize: 14, textDecoration: "none", fontWeight: 500 }}>
          I have an account
        </Link>
      </header>

      {/* Step dots */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center", padding: "12px 0", color: "var(--fg-3)", fontSize: 13, fontWeight: 600, alignItems: "center" }}>
        {STEPS.map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            style={{
              width: i === step ? 28 : 8,
              height: 8,
              borderRadius: 999,
              background: i === step ? "var(--gold-500)" : i < step ? "var(--gold-300)" : "var(--ink-300)",
              transition: "all 240ms cubic-bezier(.2,.8,.2,1)",
            }}
          />
        ))}
        <span style={{ marginLeft: 10, fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)" }}>
          {step + 1} / {STEPS.length}
        </span>
      </div>

      <main className="max-w-2xl mx-auto" style={{ padding: "48px 24px 96px" }}>

        {/* WELCOME */}
        {STEPS[step] === "welcome" && (
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 40, alignItems: "center" }}>
            <Mascot size={200} />
            <div>
              <Chip tone="gold">Step 1</Chip>
              <h1 className="font-display" style={{ fontSize: 56, lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 12px" }}>
                Pull up <em style={{ color: "var(--gold-500)" }}>a chair.</em>
              </h1>
              <p style={lede}>
                Seven quick questions and we'll build your plan. Two minutes, then your first lesson.
              </p>
              <Button variant="primary" size="lg" onClick={next} iconRight={<ArrowRight size={16} strokeWidth={1.8} />}>
                Let's go
              </Button>
            </div>
          </div>
        )}

        {/* REASON */}
        {STEPS[step] === "reason" && (
          <div>
            <Chip tone="gold">Step 2</Chip>
            <h2 className="font-display" style={stepH}>What do you want SpeakEasy to <em style={{ color: "var(--gold-500)" }}>help with</em>?</h2>
            <p style={lede}>Pick one. We use it to order your first week of lessons. You can change it later.</p>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {REASONS.map(({ id, label, desc, Icon }) => {
                const selected = reason === id;
                return (
                  <button key={id} type="button" onClick={() => setReason(id)} style={selected ? optSelected : optRest}>
                    <Icon size={20} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, textAlign: "left" }}>
                      <span style={{ display: "block", fontWeight: 700, fontSize: 14 }}>{label}</span>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 400, color: selected ? "var(--ink-700)" : "var(--fg-3)", marginTop: 2 }}>{desc}</span>
                    </span>
                    {selected && <Check size={16} strokeWidth={2} />}
                  </button>
                );
              })}
            </div>
            <FooterActions onBack={prev} onNext={next} disabled={!reason} />
          </div>
        )}

        {/* NATIVE LANGUAGE */}
        {STEPS[step] === "native" && (
          <div>
            <Chip tone="gold">Step 3</Chip>
            <h2 className="font-display" style={stepH}>What language do you <em style={{ color: "var(--gold-500)" }}>speak natively</em>?</h2>
            <p style={lede}>We'll explain things in your mother tongue while teaching English. (Flags are the one allowed pictographic exception in our system — they carry information emoji can't.)</p>
            <Card paperDeep style={{ padding: 16, maxHeight: "55vh", overflowY: "auto" }}>
              <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
                {NATIVE_LANGUAGES.map((l) => {
                  const selected = nativeCode === l.code;
                  return (
                    <button key={l.code} type="button" onClick={() => setNativeCode(l.code)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 12px",
                        background: selected ? "var(--gold-500)" : "#fff",
                        color: selected ? "var(--ink-900)" : "var(--fg-1)",
                        border: `1px solid ${selected ? "var(--gold-600)" : "var(--border-1)"}`,
                        borderRadius: "var(--radius-md)",
                        cursor: "pointer", textAlign: "left",
                        transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{l.flag}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 13, fontWeight: 700, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {l.nativeName}
                        </span>
                        <span style={{ display: "block", fontSize: 11, fontWeight: 400, opacity: 0.75, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {l.name}
                        </span>
                      </span>
                      {selected && <Check size={14} strokeWidth={2} />}
                    </button>
                  );
                })}
              </div>
            </Card>
            <p style={{ textAlign: "center", fontSize: 13, color: "var(--fg-3)", marginTop: 12 }}>
              Currently: {currentNative?.flag} <strong>{currentNative?.nativeName}</strong>
            </p>
            <FooterActions onBack={prev} onNext={next} />
          </div>
        )}

        {/* INTRO TEST */}
        {STEPS[step] === "intro-test" && (
          <div>
            <Chip tone="gold">Step 4 · quick check</Chip>
            <h2 className="font-display" style={stepH}>A 5-question <em style={{ color: "var(--gold-500)" }}>level check.</em></h2>
            <p style={lede}>Tap an answer — the test auto-advances.</p>

            <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
              {INTRO_TEST.map((_, i) => (
                <span key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < testQ ? "var(--gold-500)" : i === testQ ? "var(--gold-300)" : "var(--ink-200)" }} />
              ))}
            </div>

            <Card paperDeep style={{ padding: 24 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.14em", margin: "0 0 8px" }}>
                Question {testQ + 1} of {INTRO_TEST.length}
              </p>
              <p className="font-display" style={{ fontSize: 28, lineHeight: 1.2, fontWeight: 400, margin: "0 0 18px" }}>
                {INTRO_TEST[testQ].q}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {INTRO_TEST[testQ].options.map((opt, oi) => {
                  const myAns = testAnswers[testQ];
                  const isAnswered = myAns !== undefined;
                  const isCorrect  = isAnswered && oi === INTRO_TEST[testQ].answer;
                  const isWrongMine = isAnswered && myAns === oi && oi !== INTRO_TEST[testQ].answer;
                  let style = optionRest;
                  if (isCorrect)        style = optionCorrect;
                  else if (isWrongMine) style = optionWrong;
                  else if (isAnswered)  style = optionDim;
                  return (
                    <button key={oi} type="button" onClick={() => !isAnswered && answerTest(testQ, oi)} disabled={isAnswered} style={style}>
                      <span style={letterChip}>{String.fromCharCode(65 + oi)}</span>
                      <span style={{ flex: 1, textAlign: "left" }}>{opt}</span>
                      {isCorrect && <Check size={16} strokeWidth={1.8} color="var(--sage-dark)" />}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* LEVEL CONFIRM */}
        {STEPS[step] === "level" && (
          <div>
            <Chip tone="gold">Step 5</Chip>
            <h2 className="font-display" style={stepH}>Confirm your <em style={{ color: "var(--gold-500)" }}>starting level.</em></h2>
            {suggestedLevel && (
              <p style={lede}>
                Based on your test ({testScore}/{INTRO_TEST.length}), we suggest <strong>{LEVELS.find((l) => l.id === suggestedLevel)?.label}</strong>. You can pick another if you'd rather.
              </p>
            )}
            <div className="grid" style={{ gridTemplateColumns: "1fr", gap: 12 }}>
              {LEVELS.map((l) => {
                const selected = level === l.id;
                const isSuggested = suggestedLevel === l.id;
                return (
                  <button key={l.id} type="button" onClick={() => setLevel(l.id)} style={selected ? optSelected : optRest}>
                    <span style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: l.spine, marginRight: 12 }} />
                    <span style={{ flex: 1, textAlign: "left" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{l.label}</span>
                        {isSuggested && <Chip tone={l.tone}>Suggested</Chip>}
                      </span>
                      <span style={{ display: "block", fontSize: 13, color: selected ? "var(--ink-700)" : "var(--fg-3)", marginTop: 2 }}>{l.desc}</span>
                      <span className="font-display" style={{ display: "block", fontStyle: "italic", color: selected ? "var(--ink-700)" : "var(--gold-700)", fontSize: 13, marginTop: 4 }}>
                        Starts at Day {l.startDay}
                      </span>
                    </span>
                    {selected && <Check size={16} strokeWidth={2} />}
                  </button>
                );
              })}
            </div>
            <p style={{ marginTop: 14, fontSize: 13, color: "var(--fg-3)" }}>
              Want to be sure?{" "}
              <Link to="/placement-test" style={{ color: "var(--gold-700)", textDecoration: "underline", fontWeight: 600 }}>
                Take the full 10-question placement test
              </Link>{" "}
              instead.
            </p>
            <FooterActions onBack={prev} onNext={next} disabled={!level} />
          </div>
        )}

        {/* DAILY GOAL */}
        {STEPS[step] === "goal" && (
          <div>
            <Chip tone="gold">Step 6</Chip>
            <h2 className="font-display" style={stepH}>How long will you <em style={{ color: "var(--gold-500)" }}>show up</em>?</h2>
            <p style={lede}>Two hours a day is the sweet spot — one hour written, one hour speaking. Pick whatever's actually realistic.</p>
            <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
              {GOALS.map((g) => {
                const selected = goal === g.minutes;
                return (
                  <button key={g.minutes} type="button" onClick={() => setGoal(g.minutes)} style={selected ? goalSelected : goalRest}>
                    {g.featured && <Chip tone="gold" style={{ position: "absolute", top: -10, left: 12 }}>Best</Chip>}
                    <span className="font-display" style={{ fontSize: 28, fontWeight: 400 }}>{g.label}</span>
                    <span style={{ fontSize: 13, color: selected ? "var(--ink-700)" : "var(--fg-3)" }}>{g.desc}</span>
                    {selected && <Check size={16} strokeWidth={2} style={{ position: "absolute", top: 12, right: 12 }} />}
                  </button>
                );
              })}
            </div>
            <FooterActions onBack={prev} onNext={next} />
          </div>
        )}

        {/* BATCH PREVIEW */}
        {STEPS[step] === "batch-preview" && selectedLevelObj && (
          <div>
            <Chip tone="gold">Step 7</Chip>
            <h2 className="font-display" style={stepH}>Your first <em style={{ color: "var(--gold-500)" }}>batch.</em></h2>
            <p style={lede}>Starting from Day {startDay}. Each lesson has a written half and an oral half.</p>
            <Card paperDeep style={{ padding: 8, maxHeight: "50vh", overflowY: "auto" }}>
              {batchLessons.map((l) => (
                <div key={l.day} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: "1px solid var(--border-1)" }}>
                  <span style={{ width: 40, height: 40, borderRadius: "var(--radius-md)", background: "var(--ink-900)", color: "var(--gold-300)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-display)", fontSize: 18, flexShrink: 0 }}>
                    {l.day}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "var(--fg-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.topic}</p>
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      <Chip tone="gold" icon={<BookOpen size={11} strokeWidth={1.8} />}>Written</Chip>
                      <Chip tone="sage" icon={<Mic size={11} strokeWidth={1.8} />}>Oral</Chip>
                    </div>
                  </div>
                </div>
              ))}
              <p style={{ textAlign: "center", color: "var(--fg-3)", fontSize: 13, padding: "14px 0 6px", margin: 0, fontStyle: "italic", fontFamily: "var(--font-display)" }}>
                + {Math.max(0, lessons.length - startDay - 5)} more lessons after these.
              </p>
            </Card>
            <FooterActions onBack={prev} onNext={next} nextLabel="Looks good" />
          </div>
        )}

        {/* READY */}
        {STEPS[step] === "ready" && (
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 40, alignItems: "center" }}>
            <Mascot size={200} mood="listening" />
            <div>
              <Chip tone="gold">Done</Chip>
              <h1 className="font-display" style={{ fontSize: 52, lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 16px" }}>
                Your plan is <em style={{ color: "var(--gold-500)" }}>ready.</em>
              </h1>
              <Card paperDeep style={{ marginBottom: 24 }}>
                <SummaryRow label="Goal"   value={REASONS.find((r) => r.id === reason)?.label} />
                <SummaryRow label="Native" value={<><span style={{ marginRight: 6 }}>{currentNative?.flag}</span>{currentNative?.nativeName}</>} />
                <SummaryRow label="Level"  value={selectedLevelObj?.label} />
                <SummaryRow label="Daily"  value={GOALS.find((g) => g.minutes === goal)?.label} />
                <SummaryRow label="Start"  value={<>Day <em className="italic-display" style={{ color: "var(--gold-500)" }}>{startDay}</em></>} last />
              </Card>
              <Button variant="primary" size="lg" onClick={finish} iconRight={<ArrowRight size={16} strokeWidth={1.8} />}>
                Start learning
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function FooterActions({ onBack, onNext, disabled, nextLabel = "Continue" }) {
  return (
    <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
      {onBack && (
        <Button variant="ghost" onClick={onBack}>← Back</Button>
      )}
      <Button variant="primary" size="lg" onClick={onNext} disabled={disabled} iconRight={<ArrowRight size={16} strokeWidth={1.8} />}>
        {nextLabel}
      </Button>
    </div>
  );
}

function SummaryRow({ label, value, last }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: last ? "none" : "1px solid var(--border-1)" }}>
      <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)" }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 14, color: "var(--fg-1)" }}>{value}</span>
    </div>
  );
}

const lede = { fontSize: 16, lineHeight: 1.6, color: "var(--fg-2)", maxWidth: "56ch", margin: "0 0 24px" };
const stepH = { fontSize: 44, lineHeight: 1.1, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 12px" };

const optBase = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "16px 18px",
  borderRadius: "var(--radius-md)",
  fontFamily: "var(--font-body)",
  fontWeight: 500,
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
  fontSize: 14,
  transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
};
const optRest     = { ...optBase, background: "#fff",            color: "var(--ink-900)",  border: "1px solid var(--border-2)" };
const optSelected = { ...optBase, background: "var(--gold-500)", color: "var(--ink-900)",  border: "1px solid var(--gold-600)", boxShadow: "var(--shadow-inset-gold)" };

const goalBase = {
  position: "relative",
  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4,
  padding: 20,
  borderRadius: "var(--radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
};
const goalRest     = { ...goalBase, background: "#fff",            color: "var(--ink-900)", border: "1px solid var(--border-2)" };
const goalSelected = { ...goalBase, background: "var(--gold-500)", color: "var(--ink-900)", border: "1px solid var(--gold-600)" };

const optionBase = {
  display: "flex", alignItems: "center", gap: 12,
  width: "100%",
  padding: "14px 16px",
  borderRadius: "var(--radius-md)",
  fontFamily: "var(--font-body)",
  fontSize: 15, fontWeight: 500,
  cursor: "pointer", textAlign: "left",
  transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
};
const optionRest    = { ...optionBase, background: "#fff",            color: "var(--ink-900)",   border: "1px solid var(--border-2)" };
const optionCorrect = { ...optionBase, background: "#eef5ee",         color: "#3d5e3a",          border: "1px solid #cfe0ce", cursor: "default" };
const optionWrong   = { ...optionBase, background: "#fdf0f1",         color: "var(--berry-dark)", border: "1px solid #ddb1b6", cursor: "default" };
const optionDim     = { ...optionBase, background: "var(--paper-deep)", color: "var(--fg-3)",    border: "1px solid var(--border-1)", opacity: 0.6, cursor: "default" };
const letterChip    = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 999, background: "var(--paper-deep)", color: "var(--ink-900)", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-mono)" };
